import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { gerarPDFServico } from '../utils/pdf';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import PecaAutocomplete from '../components/PecaAutocomplete';
import InputMoeda from '../components/InputMoeda';
import { useAuth } from '../contexts/AuthContext';

const STATUS_LIST = [
  { value: 'orcamento',             label: 'Ag. Avaliação do Mecânico', cor: 'bg-gray-100 text-gray-700' },
  { value: 'aprovado',              label: 'Aprovado - ag. mecânico',  cor: 'bg-blue-100 text-blue-800' },
  { value: 'em_andamento',          label: 'Em andamento/Em conserto', cor: 'bg-orange-100 text-orange-800' },
  { value: 'aguardando_orcamento',  label: 'Enviar orçamento p/ aprovação do cliente', cor: 'bg-purple-100 text-purple-800' },
  { value: 'aguardando_cliente',    label: 'Aguardando aprovação do cliente', cor: 'bg-cyan-100 text-cyan-800' },
  { value: 'concluido',             label: 'Concluído',                cor: 'bg-green-100 text-green-800' },
  { value: 'cancelado',             label: 'Orçamento Não Aprovado',   cor: 'bg-red-100 text-red-800' },
];

// Status que o mecânico pode definir:
// aguardando_orcamento → sinaliza que terminou o diagnóstico, admin precifica
// em_andamento         → inicia o serviço após aprovação
const STATUS_MECANICO = ['aguardando_orcamento', 'em_andamento', 'concluido'];

export default function ServicoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAdmin, isProprietario, usuario } = useAuth();

  const [servico, setServico] = useState(null);
  const [config, setConfig] = useState(null);
  const [checkin, setCheckin] = useState(null);
  const [alterandoStatus, setAlterandoStatus] = useState(false);

  // Estado de edição — abre direto se vier do checkin (?editar=1)
  const [editando, setEditando] = useState(searchParams.get('editar') === '1');
  const [pecas, setPecas] = useState([]);
  const [maosDeObra, setMaosDeObra] = useState([]);
  const [garantiaAberta, setGarantiaAberta] = useState(null); // índice da peça com painel aberto
  const [ultimaGarantia, setUltimaGarantia] = useState(null); // último fornecedor/NF preenchido, para pré-preencher novas peças
  const [desconto, setDesconto] = useState(0);
  const [diagnostico, setDiagnostico] = useState('');
  const [diagnosticoMecanico, setDiagnosticoMecanico] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [mecanicos, setMecanicos] = useState([]);
  const [mecanicoSelecionado, setMecanicoSelecionado] = useState('');
  const [salvandoMecanico, setSalvandoMecanico] = useState(false);

  // Pagamentos
  const [pagamentos, setPagamentos] = useState([]);
  const [novoPagamento, setNovoPagamento] = useState({ forma: 'pix', valor: '', data: new Date().toISOString().slice(0, 10), observacao: '', parcelas: 1, valorParcela: '' });
  const [adicionandoPagamento, setAdicionandoPagamento] = useState(false);
  const [salvandoPagamento, setSalvandoPagamento] = useState(false);

  // Inputs para nova peça / mão de obra
  const [novaPeca, setNovaPeca] = useState({ descricao: '', quantidade: 1, valorUnitario: '', custoUnitario: '', numeroSerie: '' });
  const [novaMao, setNovaMao] = useState({ descricao: '', valor: '', custoUnitario: '' });
  const [pecasAdicionadasNaSessao, setPecasAdicionadasNaSessao] = useState(false);

  const carregar = () => {
    api.get(`/servicos/${id}`).then(r => {
      setServico(r.data);
      setPecas(r.data.pecas || []);
      setMaosDeObra(r.data.maosDeObra || []);
      setDesconto(r.data.desconto || 0);
      setDiagnostico(r.data.diagnostico || '');
      setDiagnosticoMecanico(r.data.diagnosticoMecanico || '');
      setObservacoes(r.data.observacoes || '');
      setMecanicoSelecionado(r.data.responsavel || '');
      setPagamentos(r.data.pagamentos || []);
    });
    api.get('/configuracao').then(r => setConfig(r.data));
    api.get(`/servicos/${id}/checkin`).then(r => setCheckin(r.data)).catch(() => setCheckin(null));
  };

  useEffect(() => {
    carregar();
    if (isAdmin) api.get('/auth/usuarios').then(r => setMecanicos(r.data.filter(u => u.perfil === 'mecanico')));
  }, [id]);

  // ── Peças ──────────────────────────────────────────────────────────────────
  const removerPeca = i => setPecas(prev => prev.filter((_, idx) => idx !== i));

  const editarPeca = (i, campo, valor) => {
    if (campo === 'garantia' && valor?.fornecedor) setUltimaGarantia(valor);
    setPecas(prev => prev.map((p, idx) => {
      if (idx !== i) return p;
      const atualizada = { ...p, [campo]: (campo === 'descricao' || campo === 'numeroSerie' || campo === 'garantia' || valor === undefined) ? valor : Number(valor) };
      // Sugere o custo igual ao valor de venda enquanto o usuário não editar o custo diretamente
      // (funciona independente da ordem de preenchimento — só para de sincronizar quando o
      // usuário mexe no campo Custo, marcando-o como definido manualmente).
      if (campo === 'custoUnitario') atualizada._custoManual = true;
      if (campo === 'valorUnitario' && !p._custoManual) atualizada.custoUnitario = atualizada.valorUnitario;
      atualizada.valorTotal = atualizada.quantidade * atualizada.valorUnitario;
      return atualizada;
    }));
  };

  // Copia a garantia (fornecedor/NF/data/dias) da peça `i` para as demais peças
  // que ainda não têm garantia preenchida — evita retrabalho quando várias
  // peças vieram na mesma nota fiscal.
  const aplicarGarantiaATodas = i => {
    const garantia = pecas[i]?.garantia;
    if (!garantia?.fornecedor) return toast.error('Preencha ao menos o fornecedor antes de aplicar');
    setPecas(prev => prev.map((p, idx) => (idx === i || p.garantia?.fornecedor) ? p : { ...p, garantia }));
    toast.success('Garantia aplicada às demais peças');
  };

  const adicionarPeca = () => {
    if (!novaPeca.descricao) return;
    const nova = {
      ...novaPeca,
      quantidade: Number(novaPeca.quantidade) || 1,
      valorUnitario: novaPeca.valorUnitario !== '' ? Number(novaPeca.valorUnitario) : 0,
      valorTotal: (Number(novaPeca.quantidade) || 1) * (Number(novaPeca.valorUnitario) || 0),
      custoUnitario: novaPeca.custoUnitario !== '' ? Number(novaPeca.custoUnitario) : undefined,
      ...(ultimaGarantia ? { garantia: ultimaGarantia } : {}),
    };
    setPecas(prev => [...prev, nova]);
    setNovaPeca({ descricao: '', quantidade: 1, valorUnitario: '', custoUnitario: '', numeroSerie: '' });
    setPecasAdicionadasNaSessao(true);
  };

  // ── Mão de obra ────────────────────────────────────────────────────────────
  const removerMao = i => setMaosDeObra(prev => prev.filter((_, idx) => idx !== i));

  const editarMao = (i, campo, valor) =>
    setMaosDeObra(prev => prev.map((m, idx) => {
      if (idx !== i) return m;
      const atualizado = { ...m, [campo]: (campo === 'descricao' || valor === undefined) ? valor : Number(valor) };
      // Sugere o custo igual ao valor cobrado enquanto o usuário não editar o custo diretamente
      // (mesmo padrão usado em peças — funciona independente da ordem de preenchimento).
      if (campo === 'custoUnitario') atualizado._custoManual = true;
      if (campo === 'valor' && !m._custoManual) atualizado.custoUnitario = atualizado.valor;
      return atualizado;
    }));

  const adicionarMao = () => {
    if (!novaMao.descricao) return;
    setMaosDeObra(prev => [...prev, {
      descricao: novaMao.descricao,
      valor: novaMao.valor !== '' ? Number(novaMao.valor) : 0,
      custoUnitario: novaMao.custoUnitario !== '' ? Number(novaMao.custoUnitario) : undefined,
    }]);
    setNovaMao({ descricao: '', valor: '', custoUnitario: '' });
    setPecasAdicionadasNaSessao(true);
  };

  // ── Totais calculados ──────────────────────────────────────────────────────
  const totalPecasEdit = pecas.reduce((a, p) => a + (p.valorTotal || 0), 0);
  const totalMaoEdit = maosDeObra.reduce((a, m) => a + (m.valor || 0), 0);
  const totalGeralEdit = totalPecasEdit + totalMaoEdit - Number(desconto || 0);

  // ── Salvar edição ──────────────────────────────────────────────────────────
  const salvarEdicao = async () => {
    setSalvando(true);
    try {
      const payload = { pecas, maosDeObra, desconto: Number(desconto), diagnostico, diagnosticoMecanico, observacoes };
      // Mecânico adicionando peças/serviços: retorna para aprovação do admin
      if (!isAdmin) payload.status = 'aguardando_orcamento';
      await api.put(`/servicos/${id}`, payload);
      toast.success(!isAdmin ? 'Peças salvas! Aguardando aprovação do administrativo.' : 'Serviço atualizado!');
      setEditando(false);
      carregar();
    } catch { toast.error('Erro ao salvar'); }
    finally { setSalvando(false); }
  };

  const salvarMecanico = async () => {
    if (finalizado) return toast.error('OS finalizada — não é possível alterar o mecânico responsável');
    setSalvandoMecanico(true);
    try {
      await api.put(`/servicos/${id}`, { responsavel: mecanicoSelecionado });
      setServico(s => ({ ...s, responsavel: mecanicoSelecionado }));
      toast.success('Mecânico atribuído!');
    } catch { toast.error('Erro ao atribuir mecânico'); }
    finally { setSalvandoMecanico(false); }
  };

  const cancelarEdicao = () => {
    setPecas(servico.pecas || []);
    setMaosDeObra(servico.maosDeObra || []);
    setDesconto(servico.desconto || 0);
    setDiagnostico(servico.diagnostico || '');
    setDiagnosticoMecanico(servico.diagnosticoMecanico || '');
    setObservacoes(servico.observacoes || '');
    setEditando(false);
  };

  // ── Status ─────────────────────────────────────────────────────────────────
  const atualizarStatus = async novoStatus => {
    const payload = { status: novoStatus };
    // Mecânico ou proprietário iniciando execução: registra seu nome como responsável
    if ((!isAdmin || isProprietario) && novoStatus === 'em_andamento') {
      payload.responsavel = usuario.nome;
    }
    const { data } = await api.put(`/servicos/${id}`, payload);
    setServico(data); setAlterandoStatus(false);
    toast.success('Status atualizado');
    if (isAdmin && novoStatus === 'concluido') {
      toast('💰 Lembre-se de registrar a condição de pagamento desta OS!', { duration: 6000, icon: '⚠️' });
    }
    // Admin aprovando orçamento para o cliente: fica na tela para poder enviar pelo WhatsApp
    if (isAdmin && novoStatus === 'aguardando_cliente') return;
    setTimeout(() => navigate('/'), 1000);
  };

  // Síncrono e sem `await` antes do download/pop-up: em tablets e navegadores móveis,
  // qualquer espera assíncrona (mesmo uma chamada de API rápida) faz o navegador não
  // reconhecer mais a ação como parte do clique do usuário, e bloqueia silenciosamente
  // tanto o download do PDF quanto o window.open do WhatsApp.
  const enviarWhatsApp = async () => {
    const num = servico.cliente?.telefone?.replace(/\D/g, '');
    const texto = `Olá ${servico.cliente?.nome}! Seu orçamento nº ${servico.numero} está pronto.\n\nVeículo: ${servico.veiculo?.marca} ${servico.veiculo?.modelo} (${servico.veiculo?.placa})\nTotal: R$ ${servico.totalGeral?.toFixed(2)}\n\nAguardamos sua avaliação/aprovação para seguirmos com o serviço.`;
    let resultado;
    try {
      // Passa o texto junto: quando o menu nativo de compartilhamento (Web Share API
      // com arquivo) é suportado, PDF + texto padrão vão juntos numa única ação —
      // o usuário só escolhe o WhatsApp na lista e o anexo já vem preenchido.
      resultado = await gerarPDFServico(servico, config, { text: texto, title: `Orçamento nº ${servico.numero}` });
      if (!resultado?.ok) {
        toast.error(`Não foi possível gerar o PDF: ${resultado?.erro?.message || 'erro desconhecido'}`, { duration: 8000 });
      }
    } catch (err) {
      console.error('[PDF] Erro ao montar o orçamento:', err);
      toast.error(`Erro ao gerar o PDF: ${err.message}`, { duration: 8000 });
    }
    // Se o compartilhamento nativo já cuidou de tudo (PDF + texto na mesma ação),
    // não abre o WhatsApp de novo por cima — só cai para o link wa.me (texto avulso,
    // PDF baixado à parte) quando o navegador não suporta compartilhar arquivos.
    if (resultado?.metodo === 'compartilhar' || resultado?.metodo === 'compartilhar-nativo' || resultado?.metodo === 'cancelado') return;
    // Navegação direta (não window.open): em atalhos "adicionados à tela inicial",
    // window.open() pode reportar sucesso sem realmente abrir nada visível. Navegar
    // direto para o link wa.me faz o Android interceptar e abrir o WhatsApp de verdade.
    window.location.href = `https://wa.me/55${num}?text=${encodeURIComponent(texto)}`;
  };

  const handleGerarPDF = async () => {
    try {
      const resultado = await gerarPDFServico(servico, config);
      if (!resultado?.ok) {
        toast.error(`Não foi possível gerar o PDF: ${resultado?.erro?.message || 'erro desconhecido'}`, { duration: 8000 });
      }
    } catch (err) {
      console.error('[PDF] Erro ao montar o orçamento:', err);
      toast.error(`Erro ao gerar o PDF: ${err.message}`, { duration: 8000 });
    }
  };

  const deletar = async () => {
    if (!window.confirm('Excluir este serviço?')) return;
    try {
      await api.delete(`/servicos/${id}`);
      toast.success('Serviço excluído'); navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Não foi possível excluir o serviço');
    }
  };

  if (!servico) return <div className="flex items-center justify-center h-64 text-gray-500">Carregando...</div>;
  const statusAtual = STATUS_LIST.find(s => s.value === servico.status);
  // Para o proprietário, "orcamento" não é "aguardando mecânico" — é ele mesmo montando o orçamento
  const labelStatus = (isProprietario && servico.status === 'orcamento') ? 'Orçamento em elaboração' : statusAtual?.label;
  const salvarPagamento = async () => {
    const isCredito = novoPagamento.forma === 'cartao_credito';
    if (isCredito) {
      if (!novoPagamento.valorParcela || Number(novoPagamento.valorParcela) <= 0) return toast.error('Informe o valor da parcela');
      if (!novoPagamento.parcelas || Number(novoPagamento.parcelas) < 1) return toast.error('Informe o número de parcelas');
    } else {
      if (!novoPagamento.valor || Number(novoPagamento.valor) <= 0) return toast.error('Informe o valor');
    }
    const valorTotal = isCredito
      ? Number(novoPagamento.valorParcela) * Number(novoPagamento.parcelas)
      : Number(novoPagamento.valor);
    if (valorTotal > saldoPendente + 0.01) {
      return toast.error(`Valor excede o saldo pendente (R$ ${saldoPendente.toFixed(2)})`);
    }
    setSalvandoPagamento(true);
    const novo = {
      ...novoPagamento,
      valor: valorTotal,
      parcelas: isCredito ? Number(novoPagamento.parcelas) : undefined,
      valorParcela: isCredito ? Number(novoPagamento.valorParcela) : undefined,
      id: Date.now().toString(),
    };
    const lista = [...pagamentos, novo];
    await api.put(`/servicos/${id}`, { pagamentos: lista });
    setPagamentos(lista);
    setNovoPagamento({ forma: 'pix', valor: '', data: new Date().toISOString().slice(0, 10), observacao: '', parcelas: 1, valorParcela: '' });
    setAdicionandoPagamento(false);
    setSalvandoPagamento(false);
    toast.success('Pagamento registrado');
  };

  const removerPagamento = async (pid) => {
    if (saldoPendente <= 0) return toast.error('OS já quitada — não é possível alterar a condição de pagamento');
    const lista = pagamentos.filter(p => p.id !== pid);
    await api.put(`/servicos/${id}`, { pagamentos: lista });
    setPagamentos(lista);
    toast.success('Pagamento removido');
  };

  const concluido = servico.status === 'concluido';
  const cancelado = servico.status === 'cancelado';
  const finalizado = concluido || cancelado;
  // Saldo ainda não pago desta OS — nenhum pagamento novo pode ultrapassá-lo
  const totalPagoAtual = pagamentos.reduce((a, p) => a + (p.valor || 0), 0);
  const saldoPendente = Math.max((servico.totalGeral || 0) - totalPagoAtual, 0);
  const STATUS_ADMIN = STATUS_LIST.filter(s => s.value !== 'orcamento');
  // Proprietário: fluxo sem etapas de aprovação interna (sem aguardando_orcamento nem aprovado)
  const STATUS_PROPRIETARIO = STATUS_LIST.filter(s => !['orcamento', 'aguardando_orcamento', 'aprovado'].includes(s.value));
  // Mecânico só pode "Enviar orçamento p/ aprovação do cliente" se adicionou peça/serviço nesta sessão de edição
  const statusMecanicoDisponiveis = STATUS_MECANICO.filter(v => v !== 'aguardando_orcamento' || pecasAdicionadasNaSessao);
  const statusDisponiveis = isProprietario ? STATUS_PROPRIETARIO : isAdmin ? STATUS_ADMIN : STATUS_LIST.filter(s => statusMecanicoDisponiveis.includes(s.value));

  // Validação para liberar execução: todas as peças e serviços precisam ter valor > 0
  const pecasSemValor = (servico.pecas || []).filter(p => !p.valorUnitario || p.valorUnitario <= 0);
  const maoSemValor = (servico.maosDeObra || []).filter(m => !m.valor || m.valor <= 0);
  const podeLiberarExecucao = pecasSemValor.length === 0 && maoSemValor.length === 0 && ((servico.pecas?.length || 0) + (servico.maosDeObra?.length || 0)) > 0;
  // Peças sem preço (aguardando precificação pelo admin)
  const pecasSemPreco = servico.pecas?.some(p => !p.valorUnitario || p.valorUnitario === 0);

  return (
    <div className="max-w-2xl mx-auto">
      <button
        onClick={() => navigate(location.state?.from || '/')}
        className="text-blue-600 text-sm mb-4 hover:underline"
      >
        ← Voltar
      </button>

      {/* Cabeçalho */}
      <div className="bg-white rounded-xl shadow-sm p-5 mb-4">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Serviço #{servico.numero}</h1>
            <p className="text-gray-500 text-sm mt-0.5">
              {format(new Date(servico.dataEntrada), "dd/MM/yyyy", { locale: ptBR })}
              {servico.responsavel && ` • ${servico.responsavel}`}
            </p>
          </div>
          <span className={`text-sm font-medium px-3 py-1 rounded-full ${statusAtual?.cor}`}>{labelStatus}</span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-400 uppercase font-medium mb-1">Cliente</p>
            <p className="font-semibold text-gray-800">{servico.cliente?.nome}</p>
            <p className="text-sm text-gray-500">{servico.cliente?.telefone}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 uppercase font-medium mb-1">Veículo</p>
            <p className="font-semibold text-gray-800">{servico.veiculo?.placa}</p>
            <p className="text-sm text-gray-500">{servico.veiculo?.marca} {servico.veiculo?.modelo} {servico.veiculo?.ano}</p>
            {servico.kmEntrada && <p className="text-xs text-gray-400">KM: {Number(servico.kmEntrada).toLocaleString()}</p>}
          </div>
        </div>
        {servico.veiculoId && (
          <button
            onClick={() => navigate(`/historico/${servico.veiculoId}`)}
            className="mt-3 w-full border border-blue-200 text-blue-600 py-2 rounded-lg text-sm font-medium hover:bg-blue-50 flex items-center justify-center gap-1.5"
          >
            📋 Ver Histórico do Veículo (peças, garantias e orçamentos anteriores)
          </button>
        )}
        {!isProprietario && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs text-gray-400 uppercase font-medium mb-1">Mecânico Responsável</p>
          {isAdmin && !finalizado ? (
            <div className="flex items-center gap-2">
              <select
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={mecanicoSelecionado}
                onChange={e => setMecanicoSelecionado(e.target.value)}
              >
                <option value="">— Não atribuído —</option>
                {mecanicos.map(m => (
                  <option key={m.id} value={m.nome}>{m.nome}</option>
                ))}
              </select>
              <button
                onClick={salvarMecanico}
                disabled={salvandoMecanico || mecanicoSelecionado === (servico.responsavel || '')}
                className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-40 whitespace-nowrap"
              >
                {salvandoMecanico ? '...' : 'Atribuir'}
              </button>
            </div>
          ) : (
            <p className="font-semibold text-blue-700 flex items-center gap-1.5">
              🔧 {servico.responsavel || '—'}
              {isAdmin && finalizado && <span className="text-xs text-gray-400 font-normal">(🔒 OS {cancelado ? 'encerrada' : 'concluída'} — não pode ser alterado)</span>}
            </p>
          )}
        </div>
        )}
      </div>

      {/* Alerta pagamento pendente — inclui pagamento parcial, que ainda não está 100% quitado */}
      {isAdmin && concluido && saldoPendente > 0.01 && (
        <div className="mb-4 bg-orange-50 border-2 border-orange-400 rounded-xl px-4 py-3 flex items-start gap-3">
          <span className="text-xl">💰</span>
          <div>
            <p className="text-sm font-bold text-orange-800">
              {pagamentos.length === 0 ? 'Pagamento não registrado' : `Pagamento parcial — falta R$ ${saldoPendente.toFixed(2)}`}
            </p>
            <p className="text-xs text-orange-700 mt-0.5">Registre a condição de pagamento desta OS para consolidar corretamente o painel financeiro.</p>
          </div>
        </div>
      )}

      {/* Badge aguardando precificação */}
      {pecasSemPreco && (
        <div className="mb-4 bg-purple-50 border border-purple-200 rounded-xl px-4 py-2.5 text-sm text-purple-800 flex items-center gap-2">
          ⏳ Peças aguardando precificação pelo administrativo
        </div>
      )}

      {/* ── BOTÃO CHECKIN — sempre visível no topo ───────────────────────── */}
      {!editando && (
        <div className="mb-4">
          <button
            onClick={() => !concluido && navigate(`/checkin/${id}`)}
            className={`w-full py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors ${
              concluido
                ? 'bg-gray-50 border-2 border-gray-200 text-gray-400 cursor-not-allowed'
                : checkin
                  ? 'bg-green-50 border-2 border-green-500 text-green-700 hover:bg-green-100'
                  : 'bg-orange-500 text-white hover:bg-orange-600 shadow-md'
            }`}
          >
            {concluido
              ? <>{checkin ? '✅ Checking realizado' : '🔍 Checking'} — 🔒 bloqueado</>
              : checkin
                ? <>✅ Checking realizado — <span className="underline">ver / editar</span></>
                : <>🔍 Fazer Checking do Veículo</>
            }
          </button>
          {checkin && !concluido && (
            <div className="mt-1.5 grid grid-cols-3 gap-2 text-center text-xs">
              <div className="bg-gray-50 rounded-lg py-1.5 text-gray-600"><div className="font-bold text-gray-800">{checkin.marcadores?.length || 0}</div>dano(s)</div>
              <div className="bg-gray-50 rounded-lg py-1.5 text-gray-600"><div className="font-bold text-gray-800">{checkin.combustivel ?? '—'}%</div>combustível</div>
              <div className="bg-gray-50 rounded-lg py-1.5 text-gray-600"><div className="font-bold text-gray-800">{Object.values(checkin.checklist || {}).filter(v => v === 'avariado').length}</div>avaria(s)</div>
            </div>
          )}
        </div>
      )}

      {/* ── MODO VISUALIZAÇÃO ─────────────────────────────────────────────── */}
      {!editando ? (
        <>
          {/* Diagnóstico */}
          {(servico.diagnostico || servico.diagnosticoMecanico) && (
            <div className="bg-white rounded-xl shadow-sm p-4 mb-4 space-y-3">
              {servico.diagnostico && (
                <div>
                  <p className="text-xs text-gray-400 uppercase font-medium mb-1">Diagnóstico / Problema Relatado — Cliente</p>
                  <p className="text-sm text-gray-700">{servico.diagnostico}</p>
                </div>
              )}
              {servico.diagnosticoMecanico && (
                <div>
                  <p className="text-xs text-gray-400 uppercase font-medium mb-1">Diagnóstico — Mecânico</p>
                  <p className="text-sm text-gray-700">{servico.diagnosticoMecanico}</p>
                </div>
              )}
            </div>
          )}

          {/* Peças */}
          {servico.pecas?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
              <h2 className="font-semibold text-gray-700 mb-3">Peças e Materiais</h2>
              <div className="space-y-2">
                {servico.pecas.map((p, i) => (
                  <div key={i} className="flex justify-between text-sm py-1 border-b border-gray-50 last:border-0">
                    <div>
                      <span className="text-gray-700">{p.descricao} <span className="text-gray-400">({p.quantidade}x)</span></span>
                      {p.numeroSerie && <div className="text-xs text-gray-400 mt-0.5">Nº Série: <span className="font-mono">{p.numeroSerie}</span></div>}
                      {p.garantia?.fornecedor && (
                        <div className="text-xs text-blue-600 mt-0.5 flex flex-wrap gap-x-3">
                          <span>🛡 {p.garantia.fornecedor}</span>
                          {p.garantia.notaFiscal && <span>NF: {p.garantia.notaFiscal}</span>}
                          {p.garantia.dataCompra && <span>Compra: {new Date(p.garantia.dataCompra + 'T00:00:00').toLocaleDateString('pt-BR')}</span>}
                          {p.garantia.diasGarantia && (() => {
                            const venc = new Date(p.garantia.dataCompra + 'T00:00:00');
                            venc.setDate(venc.getDate() + Number(p.garantia.diasGarantia));
                            const diff = Math.ceil((venc - new Date()) / 86400000);
                            const cor = diff < 0 ? 'text-red-500' : diff < 30 ? 'text-orange-500' : 'text-green-600';
                            return <span className={cor}>{diff < 0 ? `Garantia vencida (${Math.abs(diff)}d)` : `Garantia até ${venc.toLocaleDateString('pt-BR')}`}</span>;
                          })()}
                        </div>
                      )}
                    </div>
                    {isAdmin && <span className="font-medium shrink-0 ml-2">R$ {p.valorTotal?.toFixed(2)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mão de obra */}
          {servico.maosDeObra?.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
              <h2 className="font-semibold text-gray-700 mb-3">Mão de Obra</h2>
              <div className="space-y-2">
                {servico.maosDeObra.map((m, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-gray-700">{m.descricao}</span>
                    {isAdmin && <span className="font-medium">R$ {m.valor?.toFixed(2)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Totais — apenas admin */}
          {isAdmin && (
            <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1"><span>Total Peças</span><span>R$ {servico.totalPecas?.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm text-gray-600 mb-1"><span>Total Mão de Obra</span><span>R$ {servico.totalMaoDeObra?.toFixed(2)}</span></div>
              {servico.desconto > 0 && <div className="flex justify-between text-sm text-gray-600 mb-1"><span>Desconto</span><span>-R$ {servico.desconto?.toFixed(2)}</span></div>}
              <div className="flex justify-between font-bold text-lg border-t border-gray-100 pt-2"><span>Total</span><span>R$ {servico.totalGeral?.toFixed(2)}</span></div>
            </div>
          )}

          {/* Pagamentos — apenas admin, apenas após conclusão */}
          {isAdmin && concluido && (
            <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
              {(() => {
                const totalPago = totalPagoAtual;
                const totalOS = servico.totalGeral || 0;
                const pendente = saldoPendente;
                const statusPag = totalPago === 0 ? 'pendente' : totalPago >= totalOS ? 'pago' : 'parcial';
                const FORMAS = { pix: 'PIX', cartao_credito: 'Cartão de Crédito', cartao_debito: 'Cartão de Débito', dinheiro: 'Dinheiro', cheque: 'Cheque' };
                return (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs text-gray-400 uppercase font-medium">Pagamentos</p>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${statusPag === 'pago' ? 'bg-green-100 text-green-700' : statusPag === 'parcial' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                          {statusPag === 'pago' ? '✅ Pago' : statusPag === 'parcial' ? '⚠️ Parcial' : '🔴 Pendente'}
                        </span>
                        {!adicionandoPagamento && statusPag !== 'pago' && (
                          <button
                            onClick={() => {
                              setNovoPagamento(p => ({ ...p, valor: pendente > 0 ? parseFloat(pendente.toFixed(2)) : '' }));
                              setAdicionandoPagamento(true);
                            }}
                            className="text-xs bg-blue-600 text-white px-2.5 py-1 rounded-lg hover:bg-blue-700">+ Pagamento</button>
                        )}
                      </div>
                    </div>

                    {adicionandoPagamento && (
                      <div className="bg-gray-50 rounded-xl p-3 mb-3 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">Forma</label>
                            <select value={novoPagamento.forma}
                              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              onChange={e => {
                                const forma = e.target.value;
                                const valorParcela = forma === 'cartao_credito' && pendente > 0
                                  ? parseFloat(pendente.toFixed(2)) : '';
                                setNovoPagamento(p => ({ ...p, forma, parcelas: 1, valorParcela, valor: pendente > 0 ? parseFloat(pendente.toFixed(2)) : '' }));
                              }}>
                              {Object.entries(FORMAS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">Data</label>
                            <input type="date" value={novoPagamento.data} onChange={e => setNovoPagamento(p => ({ ...p, data: e.target.value }))}
                              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                          </div>
                        </div>
                        {novoPagamento.forma === 'cartao_credito' ? (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-xs text-gray-500 mb-1 block">Nº de Parcelas</label>
                              <select value={novoPagamento.parcelas}
                                onChange={e => {
                                  const n = Number(e.target.value);
                                  const auto = pendente > 0 ? pendente / n : 0;
                                  setNovoPagamento(p => ({ ...p, parcelas: n, valorParcela: parseFloat(auto.toFixed(2)) }));
                                }}
                                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                                {[1,2,3,4,5,6,7,8,9,10,11,12,18,24].map(n => (
                                  <option key={n} value={n}>{n}x</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="text-xs text-gray-500 mb-1 block">Valor por Parcela (R$)</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={novoPagamento.valorParcela}
                                onChange={e => setNovoPagamento(p => ({ ...p, valorParcela: parseFloat(e.target.value) || 0 }))}
                                className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            {novoPagamento.parcelas > 0 && novoPagamento.valorParcela > 0 && (() => {
                              const totalParcelado = novoPagamento.parcelas * novoPagamento.valorParcela;
                              const excede = totalParcelado > pendente + 0.01;
                              return (
                                <div className={`col-span-2 rounded-lg px-3 py-1.5 text-xs font-medium ${excede ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
                                  Total: R$ {totalParcelado.toFixed(2)}
                                  {excede && ` — excede o saldo pendente (R$ ${pendente.toFixed(2)})`}
                                </div>
                              );
                            })()}
                          </div>
                        ) : (
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">Valor (R$) — saldo pendente: R$ {pendente.toFixed(2)}</label>
                            <InputMoeda value={novoPagamento.valor} onChange={v => setNovoPagamento(p => ({ ...p, valor: v }))}
                              className={`w-full border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 ${
                                Number(novoPagamento.valor) > pendente + 0.01
                                  ? 'border-red-400 focus:ring-red-400'
                                  : 'border-gray-300 focus:ring-blue-500'
                              }`} />
                          </div>
                        )}
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">Observação (opcional)</label>
                          <input type="text" value={novoPagamento.observacao} onChange={e => setNovoPagamento(p => ({ ...p, observacao: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Ex: parcela 1/2" />
                        </div>
                        <div className="flex gap-2 pt-1">
                          {(() => {
                            const valorInformado = novoPagamento.forma === 'cartao_credito'
                              ? Number(novoPagamento.valorParcela || 0) * Number(novoPagamento.parcelas || 0)
                              : Number(novoPagamento.valor || 0);
                            const excede = valorInformado > pendente + 0.01;
                            return (
                              <button onClick={salvarPagamento} disabled={salvandoPagamento || excede}
                                className="flex-1 bg-blue-600 text-white py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                                {salvandoPagamento ? 'Salvando...' : 'Confirmar'}
                              </button>
                            );
                          })()}
                          <button onClick={() => setAdicionandoPagamento(false)} className="flex-1 bg-gray-100 text-gray-700 py-1.5 rounded-lg text-sm hover:bg-gray-200">
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}

                    {pagamentos.length > 0 ? (
                      <div className="space-y-2 mb-3">
                        {pagamentos.map(p => (
                          <div key={p.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                            <div>
                              <span className="text-sm font-medium text-gray-700">{FORMAS[p.forma] || p.forma}</span>
                              {p.parcelas > 1 && <span className="text-xs text-blue-600 font-semibold ml-1">{p.parcelas}x de R$ {p.valorParcela?.toFixed(2)}</span>}
                              <span className="text-xs text-gray-400 ml-2">{p.data ? format(new Date(p.data + 'T00:00:00'), 'dd/MM/yyyy') : ''}</span>
                              {p.observacao && <span className="text-xs text-gray-400 ml-2">• {p.observacao}</span>}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-green-700">R$ {p.valor?.toFixed(2)}</span>
                              {statusPag !== 'pago' && (
                                <button onClick={() => removerPagamento(p.id)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 mb-3">Nenhum pagamento registrado</p>
                    )}

                    {statusPag === 'pago' && (
                      <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-3 text-xs text-green-700 font-medium flex items-center gap-1.5">
                        🔒 OS quitada — condição de pagamento não pode mais ser alterada
                      </div>
                    )}

                    <div className="border-t border-gray-100 pt-2 space-y-1">
                      <div className="flex justify-between text-sm text-gray-600"><span>Total OS</span><span>R$ {totalOS.toFixed(2)}</span></div>
                      <div className="flex justify-between text-sm text-green-700 font-medium"><span>Total Pago</span><span>R$ {totalPago.toFixed(2)}</span></div>
                      {pendente > 0 && <div className="flex justify-between text-sm text-red-600 font-medium"><span>A Receber</span><span>R$ {pendente.toFixed(2)}</span></div>}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {servico.observacoes && (
            <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
              <p className="text-xs text-gray-400 uppercase font-medium mb-1">Observações</p>
              <p className="text-sm text-gray-700">{servico.observacoes}</p>
            </div>
          )}

          {/* Botão editar — bloqueado se finalizado ou aguardando aprovação */}
          {finalizado ? (
            <div className="w-full mb-4 bg-gray-50 border-2 border-gray-200 text-gray-400 py-3 rounded-xl text-sm text-center flex items-center justify-center gap-2">
              🔒 {cancelado ? 'Orçamento não aprovado — edição bloqueada' : 'OS concluída — edição bloqueada'}
            </div>
          ) : !isAdmin && servico.status === 'aguardando_orcamento' ? (
            <div className="w-full mb-4 bg-purple-50 border-2 border-purple-200 text-purple-500 py-3 rounded-xl text-sm text-center flex items-center justify-center gap-2">
              🔒 Orçamento enviado para aprovação — edição bloqueada
            </div>
          ) : !isAdmin && ['aguardando_cliente'].includes(servico.status) ? null : (
            <button
              onClick={() => setEditando(true)}
              className="w-full mb-4 bg-amber-50 border-2 border-amber-400 text-amber-700 py-3 rounded-xl font-semibold text-sm hover:bg-amber-100 transition-colors"
            >
              ✏️ Editar Peças e Valores
            </button>
          )}
        </>
      ) : (
        /* ── MODO EDIÇÃO ──────────────────────────────────────────────────── */
        <div className="space-y-4 mb-4">
          <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-2.5 text-sm text-amber-800 font-medium flex items-center gap-2">
            ✏️ Modo de edição ativo — altere os itens e salve ao final
          </div>

          {/* Diagnóstico */}
          <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Diagnóstico / Problema Relatado — CLIENTE</label>
              <textarea rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                value={diagnostico} onChange={e => setDiagnostico(e.target.value)} placeholder="O que o cliente relatou..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Diagnóstico — MECÂNICO</label>
              <textarea rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                value={diagnosticoMecanico} onChange={e => setDiagnosticoMecanico(e.target.value)} placeholder="Problema encontrado pelo mecânico..." />
            </div>
          </div>

          {/* Peças */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h2 className="font-semibold text-gray-700 mb-3">Peças e Materiais</h2>
            <div className="space-y-2 mb-3">
              {pecas.length === 0 && <p className="text-sm text-gray-400 text-center py-2">Nenhuma peça adicionada</p>}
              {pecas.map((p, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-2 space-y-1.5">
                  {/* Linha 1: Descrição + botão remover */}
                  <div className="flex gap-1.5 items-center">
                    <PecaAutocomplete
                      className="flex-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                      value={p.descricao}
                      onChange={val => editarPeca(i, 'descricao', val)}
                      onSugestao={item => setPecas(prev => prev.map((pc, idx) => {
                        if (idx !== i) return pc;
                        const vUnit = item.ultimoValor ?? pc.valorUnitario;
                        return {
                          ...pc, descricao: item.descricao, valorUnitario: vUnit, valorTotal: pc.quantidade * vUnit,
                          custoUnitario: item.ultimoCusto ?? pc.custoUnitario,
                          _custoManual: item.ultimoCusto != null, // custo histórico real — não sobrescrever mais ao editar a venda
                        };
                      }))}
                      placeholder="Descrição"
                    />
                    <button type="button" onClick={() => removerPeca(i)} className="shrink-0 bg-red-100 text-red-600 rounded-lg px-3 py-1.5 text-sm font-bold hover:bg-red-200 transition-colors">✕</button>
                  </div>
                  {/* Linha 2: Qtd · Nº Série · Valor · Custo · Margem */}
                  <div className="flex gap-2 items-center flex-wrap">
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-400">Qtd</span>
                      <input type="number" min="1"
                        className="w-14 border border-gray-200 rounded-lg px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                        value={p.quantidade} onChange={e => editarPeca(i, 'quantidade', e.target.value)}
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-400">Nº Série</span>
                      <input type="text"
                        className="w-32 border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                        placeholder="opcional"
                        value={p.numeroSerie || ''}
                        onChange={e => editarPeca(i, 'numeroSerie', e.target.value)}
                      />
                    </div>
                    {isAdmin && (
                      <>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-400">Venda</span>
                          <InputMoeda
                            className="w-24 border border-gray-200 rounded-lg px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                            value={p.valorUnitario}
                            onChange={val => editarPeca(i, 'valorUnitario', val === '' ? 0 : val)}
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-orange-400">Custo</span>
                          <InputMoeda
                            className="w-24 border border-orange-300 rounded-lg px-2 py-1 text-sm text-right bg-orange-50 focus:outline-none focus:ring-1 focus:ring-orange-400"
                            placeholder="—"
                            value={p.custoUnitario ?? ''}
                            onChange={val => editarPeca(i, 'custoUnitario', val === '' ? undefined : val)}
                          />
                        </div>
                        {p.custoUnitario > 0 && (
                          <span className="text-xs text-gray-400 ml-1">
                            margem: {(((p.valorUnitario - p.custoUnitario) / p.valorUnitario) * 100).toFixed(0)}%
                          </span>
                        )}
                      </>
                    )}
                    {/* Botão garantia */}
                    <button
                      type="button"
                      onClick={() => setGarantiaAberta(garantiaAberta === i ? null : i)}
                      className={`ml-auto text-xs px-2 py-1 rounded-lg font-medium transition-colors whitespace-nowrap ${
                        p.garantia?.fornecedor
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {garantiaAberta === i ? '− Garantia' : p.garantia?.fornecedor ? '🛡 Garantia' : '+ Garantia'}
                    </button>
                  </div>

                  {/* Painel de garantia */}
                  {garantiaAberta === i && (
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-2">
                      <p className="text-xs font-medium text-blue-700 mb-1">Dados de garantia do fornecedor</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-gray-500">Fornecedor</label>
                          <input type="text" placeholder="Nome do fornecedor"
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white mt-0.5"
                            value={p.garantia?.fornecedor || ''}
                            onChange={e => editarPeca(i, 'garantia', { ...p.garantia, fornecedor: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Nº Nota Fiscal</label>
                          <input type="text" placeholder="Ex: 001234"
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white mt-0.5"
                            value={p.garantia?.notaFiscal || ''}
                            onChange={e => editarPeca(i, 'garantia', { ...p.garantia, notaFiscal: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Data da compra</label>
                          <input type="date"
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white mt-0.5"
                            value={p.garantia?.dataCompra || ''}
                            onChange={e => editarPeca(i, 'garantia', { ...p.garantia, dataCompra: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Garantia do fornecedor (dias)</label>
                          <input type="number" min="1" placeholder="Ex: 90"
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white mt-0.5"
                            value={p.garantia?.diasGarantia || ''}
                            onChange={e => editarPeca(i, 'garantia', { ...p.garantia, diasGarantia: e.target.value })}
                          />
                        </div>
                      </div>
                      {p.garantia?.fornecedor && pecas.length > 1 && (
                        <button type="button" onClick={() => aplicarGarantiaATodas(i)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium hover:underline">
                          📋 Aplicar a todas as peças
                        </button>
                      )}
                      {p.garantia?.dataCompra && p.garantia?.diasGarantia && (() => {
                        const vencForn = new Date(p.garantia.dataCompra);
                        vencForn.setDate(vencForn.getDate() + Number(p.garantia.diasGarantia));
                        const hoje = new Date();
                        const diff = Math.ceil((vencForn - hoje) / 86400000);
                        const cor = diff < 0 ? 'text-red-600' : diff < 30 ? 'text-orange-600' : 'text-green-700';
                        return (
                          <p className={`text-xs font-medium mt-1 ${cor}`}>
                            {diff < 0
                              ? `⚠ Garantia do fornecedor venceu há ${Math.abs(diff)} dias`
                              : `✓ Garantia do fornecedor válida por mais ${diff} dias (vence ${vencForn.toLocaleDateString('pt-BR')})`}
                          </p>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* Nova peça */}
            <div className="border-t border-dashed border-gray-200 pt-3 space-y-2">
              {/* Linha 1: Descrição */}
              <PecaAutocomplete
                className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={novaPeca.descricao}
                onChange={val => setNovaPeca(p => ({ ...p, descricao: val }))}
                onSugestao={item => setNovaPeca(p => ({
                  ...p,
                  descricao: item.descricao,
                  valorUnitario: item.ultimoValor ? String(item.ultimoValor) : p.valorUnitario,
                  custoUnitario: item.ultimoCusto ? String(item.ultimoCusto) : p.custoUnitario,
                }))}
                placeholder="Nome da peça ou material..."
              />
              {/* Linha 2: Qtd · Venda · Custo · Add */}
              {isAdmin ? (
                <div className="grid grid-cols-12 gap-1.5 items-center">
                  <input type="number" min="1" className="col-span-2 border border-gray-300 rounded-lg px-2 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Qtd"
                    value={novaPeca.quantidade} onChange={e => setNovaPeca(p => ({ ...p, quantidade: e.target.value }))} />
                  <InputMoeda
                    className="col-span-3 border border-gray-300 rounded-lg px-2 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="R$ Venda"
                    value={novaPeca.valorUnitario}
                    onChange={val => setNovaPeca(p => ({ ...p, valorUnitario: val, custoUnitario: p._custoManual ? p.custoUnitario : val }))}
                  />
                  <InputMoeda
                    className="col-span-3 border border-orange-300 rounded-lg px-2 py-2 text-sm bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    placeholder="R$ Custo"
                    value={novaPeca.custoUnitario}
                    onChange={val => setNovaPeca(p => ({ ...p, custoUnitario: val, _custoManual: true }))}
                  />
                  <button type="button" onClick={adicionarPeca} className="col-span-4 bg-blue-600 text-white rounded-lg py-2 text-sm font-bold hover:bg-blue-700">+</button>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <div className="grid grid-cols-12 gap-1.5 items-center">
                    <input type="number" min="1" className="col-span-3 border border-gray-300 rounded-lg px-2 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Qtd"
                      value={novaPeca.quantidade} onChange={e => setNovaPeca(p => ({ ...p, quantidade: e.target.value }))} />
                    <input type="text" className="col-span-9 border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Nº de série (opcional)"
                      value={novaPeca.numeroSerie} onChange={e => setNovaPeca(p => ({ ...p, numeroSerie: e.target.value }))} />
                  </div>
                  <button type="button" onClick={adicionarPeca} className="w-full bg-blue-600 text-white rounded-lg py-2 text-sm font-bold hover:bg-blue-700">+ Adicionar Peça</button>
                </div>
              )}
              {isAdmin && <p className="text-xs text-orange-500">* Custo interno — não aparece no orçamento</p>}
            </div>
          </div>

          {/* Mão de obra */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h2 className="font-semibold text-gray-700 mb-3">Mão de Obra</h2>
            <div className="space-y-2 mb-3">
              {maosDeObra.length === 0 && <p className="text-sm text-gray-400 text-center py-2">Nenhuma mão de obra adicionada</p>}
              {maosDeObra.map((m, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-2 space-y-1.5">
                  {/* Linha 1: Descrição + botão remover */}
                  <div className="flex gap-1.5 items-center">
                    <input className="flex-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                      value={m.descricao} onChange={e => editarMao(i, 'descricao', e.target.value)} />
                    <button type="button" onClick={() => removerMao(i)} className="shrink-0 bg-red-100 text-red-600 rounded-lg px-3 py-1.5 text-sm font-bold hover:bg-red-200 transition-colors">✕</button>
                  </div>
                  {/* Linha 2: Valor · Custo · Margem — apenas admin */}
                  {isAdmin && (
                    <div className="flex gap-2 items-center flex-wrap">
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-400">Valor</span>
                        <InputMoeda
                          className="w-24 border border-gray-200 rounded-lg px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                          value={m.valor}
                          onChange={val => editarMao(i, 'valor', val === '' ? 0 : val)}
                        />
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-orange-400">Custo</span>
                        <InputMoeda
                          className="w-24 border border-orange-300 rounded-lg px-2 py-1 text-sm text-right bg-orange-50 focus:outline-none focus:ring-1 focus:ring-orange-400"
                          placeholder="—"
                          value={m.custoUnitario ?? ''}
                          onChange={val => editarMao(i, 'custoUnitario', val === '' ? undefined : val)}
                        />
                      </div>
                      {m.custoUnitario > 0 && (
                        <span className="text-xs text-gray-400 ml-1">
                          margem: {(((m.valor - m.custoUnitario) / m.valor) * 100).toFixed(0)}%
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {isAdmin && <p className="text-xs text-orange-500 mb-2">* Custo interno (repasse ao mecânico) — não aparece no orçamento</p>}
            <div className="grid grid-cols-12 gap-1.5 items-center border-t border-dashed border-gray-200 pt-3">
              <input className={`border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isAdmin ? 'col-span-5' : 'col-span-10'}`} placeholder="Novo serviço..."
                value={novaMao.descricao} onChange={e => setNovaMao(m => ({ ...m, descricao: e.target.value }))} />
              {isAdmin && (
                <>
                  <InputMoeda
                    className="col-span-3 border border-gray-300 rounded-lg px-2 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="R$ Valor"
                    value={novaMao.valor}
                    onChange={val => setNovaMao(m => ({ ...m, valor: val, custoUnitario: m.custoUnitario === '' ? val : m.custoUnitario }))}
                  />
                  <InputMoeda
                    className="col-span-2 border border-orange-300 rounded-lg px-2 py-2 text-sm bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    placeholder="R$ Custo"
                    value={novaMao.custoUnitario}
                    onChange={val => setNovaMao(m => ({ ...m, custoUnitario: val, _custoManual: true }))}
                  />
                </>
              )}
              <button type="button" onClick={adicionarMao} className="col-span-2 bg-blue-600 text-white rounded-lg py-2 text-sm font-bold hover:bg-blue-700">+</button>
            </div>
          </div>

          {/* Totais em tempo real — apenas admin */}
          {isAdmin && (
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1"><span>Total Peças</span><span>R$ {totalPecasEdit.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm text-gray-600 mb-2"><span>Total Mão de Obra</span><span>R$ {totalMaoEdit.toFixed(2)}</span></div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-gray-600">Desconto (R$)</label>
                <InputMoeda
                  className="w-28 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={desconto}
                  onChange={val => setDesconto(val === '' ? 0 : val)}
                />
              </div>
              <div className="flex justify-between font-bold text-lg border-t border-gray-100 pt-2 text-blue-700">
                <span>Total</span><span>R$ {totalGeralEdit.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Observações */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
            <textarea rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              value={observacoes} onChange={e => setObservacoes(e.target.value)} />
          </div>

          {/* Ações edição */}
          <div className="grid grid-cols-2 gap-3">
            <button onClick={cancelarEdicao} className="border border-gray-300 text-gray-700 py-3 rounded-xl font-semibold text-sm hover:bg-gray-50">
              Cancelar
            </button>
            <button onClick={salvarEdicao} disabled={salvando} className="bg-blue-600 text-white py-3 rounded-xl font-semibold text-sm hover:bg-blue-700 disabled:opacity-50">
              {salvando ? 'Salvando...' : '💾 Salvar Alterações'}
            </button>
          </div>
        </div>
      )}

      {/* Ações gerais */}
      {!editando && (
        <div className="space-y-3">

          {concluido ? (
            /* OS concluída — nenhuma alteração, mas admin pode consultar o orçamento em PDF */
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-center gap-2 text-green-700 text-sm font-medium">
                🔒 Ordem de serviço concluída — nenhuma alteração permitida
              </div>
              {isAdmin && (
                <button onClick={handleGerarPDF} className="w-full bg-gray-800 text-white py-3 rounded-xl font-medium hover:bg-gray-900 text-sm flex items-center justify-center gap-2">
                  📄 Gerar PDF do Orçamento Aprovado
                </button>
              )}
            </div>

          ) : (isAdmin && servico.status === 'aguardando_orcamento') || (isProprietario && servico.status === 'orcamento') ? (
            /* Admin precificando (ou proprietário montando o orçamento): preenche valores e envia ao cliente */
            <div className="space-y-3">
              <div className={`rounded-xl px-4 py-3 text-sm font-medium flex items-center gap-2 ${podeLiberarExecucao ? 'bg-green-50 border border-green-300 text-green-800' : 'bg-purple-50 border border-purple-200 text-purple-800'}`}>
                {podeLiberarExecucao ? '✅ Valores atualizados — enviar Orçamento para o Cliente' : '⏳ Orçamento aguardando sua definição de valores'}
              </div>
              {!podeLiberarExecucao && (
                <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 text-sm text-amber-800 font-medium flex items-center gap-2">
                  ⚠️ Preencha os valores antes de enviar ao cliente
                </div>
              )}
              <button
                onClick={async () => {
                  if (!podeLiberarExecucao) return;
                  await atualizarStatus('aguardando_cliente');
                }}
                className={`w-full py-4 rounded-xl font-semibold text-base shadow-md flex items-center justify-center gap-2 transition-colors ${
                  podeLiberarExecucao
                    ? 'bg-cyan-600 text-white hover:bg-cyan-700 cursor-pointer'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                📄 Enviar Orçamento para o Cliente
              </button>
            </div>

          ) : isAdmin && servico.status === 'aguardando_cliente' ? (
            /* Admin aguardando aprovação do cliente: pode reenviar PDF e liberar */
            <div className="space-y-3">
              <div className="bg-cyan-50 border border-cyan-200 rounded-xl px-4 py-3 text-sm text-cyan-800 font-medium flex items-center gap-2">
                ⏳ Aguardando aprovação do cliente
              </div>
              <button
                onClick={enviarWhatsApp}
                className="w-full bg-green-500 text-white py-3 rounded-xl font-medium hover:bg-green-600 text-sm flex items-center justify-center gap-2"
              >
                📱 Enviar por WhatsApp
              </button>
              <button
                onClick={() => atualizarStatus(isProprietario ? 'em_andamento' : 'aprovado')}
                className="w-full bg-blue-600 text-white py-4 rounded-xl font-semibold text-base hover:bg-blue-700 shadow-md flex items-center justify-center gap-2"
              >
                {isProprietario ? '🔧 Cliente aprovou — Iniciar Serviço' : '✅ Liberar para Execução'}
              </button>
            </div>

          ) : !isAdmin && cancelado ? (
            /* Mecânico: OS com orçamento não aprovado — bloqueada */
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2 text-red-700 text-sm font-medium">
              ❌ Orçamento não aprovado — esta OS está encerrada
            </div>

          ) : !isAdmin && servico.status === 'aguardando_orcamento' ? (
            /* Mecânico aguardando admin precificar — bloqueado */
            <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3 flex items-center gap-2 text-purple-800 text-sm font-medium">
              ⏳ Orçamento enviado — aguardando aprovação do administrativo
            </div>

          ) : !isAdmin && servico.status === 'aguardando_cliente' ? (
            /* Mecânico aguardando aprovação do cliente — bloqueado */
            <div className="bg-cyan-50 border border-cyan-200 rounded-xl px-4 py-3 flex items-center gap-2 text-cyan-800 text-sm font-medium">
              ⏳ Aguardando aprovação do cliente
            </div>

          ) : !isAdmin && servico.status === 'orcamento' && pecasAdicionadasNaSessao ? (
            /* Mecânico em OS nova: único botão — enviar para aprovação */
            servico.diagnosticoMecanico?.trim() ? (
              <button
                onClick={() => atualizarStatus('aguardando_orcamento')}
                className="w-full bg-purple-600 text-white py-4 rounded-xl font-semibold text-base hover:bg-purple-700 shadow-md flex items-center justify-center gap-2"
              >
                📋 Enviar Orçamento para Aprovação
              </button>
            ) : (
              <div className="space-y-2">
                <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 text-sm text-amber-800 font-medium flex items-start gap-2">
                  ⚠️ Preencha o <strong>Diagnóstico do Mecânico</strong> antes de enviar para aprovação.
                </div>
                <button
                  onClick={() => setEditando(true)}
                  className="w-full bg-amber-500 text-white py-3 rounded-xl font-semibold text-sm hover:bg-amber-600"
                >
                  ✏️ Preencher Diagnóstico
                </button>
              </div>
            )

          ) : alterandoStatus ? (
            <div className="bg-white rounded-xl shadow-sm p-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Alterar Status</p>
              <div className="grid grid-cols-2 gap-2">
                {statusDisponiveis.map(s => (
                  <button key={s.value} onClick={() => atualizarStatus(s.value)}
                    className={`py-2.5 rounded-lg text-sm font-medium ${s.cor} border-2 ${servico.status === s.value ? 'border-gray-400' : 'border-transparent'}`}>
                    {s.label}
                  </button>
                ))}
              </div>
              <button onClick={() => setAlterandoStatus(false)} className="w-full mt-2 text-sm text-gray-500 py-2">Cancelar</button>
            </div>

          ) : (
            <button onClick={() => setAlterandoStatus(true)} className="w-full bg-white border border-gray-200 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-50 shadow-sm">
              Alterar Status
            </button>
          )}

          {/* PDF + WhatsApp — admin, fora dos estados com botões próprios */}
          {isAdmin && !['aguardando_orcamento', 'aguardando_cliente'].includes(servico.status) && !concluido && (
            <div className="grid grid-cols-2 gap-3">
              <button onClick={handleGerarPDF} className="bg-gray-800 text-white py-3 rounded-xl font-medium hover:bg-gray-900 text-sm">📄 Gerar PDF</button>
              <button onClick={enviarWhatsApp} className="bg-green-500 text-white py-3 rounded-xl font-medium hover:bg-green-600 text-sm">📱 WhatsApp</button>
            </div>
          )}

          {isAdmin && !concluido && (
            <button onClick={deletar} className="w-full text-red-500 py-2.5 rounded-xl text-sm font-medium hover:bg-red-50">Excluir Serviço</button>
          )}
        </div>
      )}
    </div>
  );
}
